/* eslint-disable jsx-a11y/alt-text */
'use client'

import {
  Anchor,
  Image,
  OpeningTitle,
  Paragraph,
  Section,
  Title,
} from '@dynamo-caffeina-dev/airc-ui-kit'
import NextImage from 'next/image'
import Link from 'next/link'

import LinkArrowRight from '@/components/atoms/Icons/link-arrow'
import {
  ImageTextModelType,
  LayoutDirectionType,
  LayoutDistributionType,
  NormalizedSection,
} from '@/types'
import { CSSProperties } from 'react'

import { useParams } from 'next/navigation'

const PARTNER_SLUGS = ['partner', 'partners']

export interface ImageTextProps extends NormalizedSection {
  modelledContent: ImageTextModelType
  props?: {
    bg?: string | 'white' | 'dark' | 'black' | 'grey-050'
    containerBg?: string
    direction: LayoutDirectionType
    distribution: LayoutDistributionType
    hideSection: boolean
  }
}

function ImageText({ modelledContent, props }: ImageTextProps) {
  const params = useParams()
  // In case of multiple path segments, we just check the 1st one
  // Also, the site can only go 1 level deep
  const slug = Array.isArray(params.slug) ? params.slug[0] : params.slug

  const { openingTitle, title, paragraph, cta, image } = modelledContent
  const { fit, imageProps } = image
  const { bg, containerBg, direction, distribution } = props || {
    bg: 'white',
  }

  const openingTitleLabel = openingTitle?.content
  const titleLabel = title?.content
  const paragraphContent = paragraph?.content
  const ctaLabel = cta?.label

  return (
    <Section
      className={`section-image-text`}
      data-container-bg={!!containerBg}
      data-direction={direction ?? 'row'}
      data-distribution={distribution ?? 'text-image'}
      {...(PARTNER_SLUGS.includes(slug)
        ? { 'data-section-subtype': slug }
        : {})}
      style={
        {
          '--container-bg': `var(--airc-ui__colors-${containerBg})`,
          ...(bg !== 'white' && { backgroundColor: bg }),
        } as CSSProperties
      }
    >
      <div className={`section-image-text__content-wrapper`}>
        <div className={`section-image-text__content-wrapper__title-wrapper `}>
          {openingTitleLabel ? (
            <OpeningTitle
              className={`section-image-text__content-wrapper__title-wrapper__opening-title`}
              {...openingTitle}
            >
              {openingTitleLabel}
            </OpeningTitle>
          ) : null}

          {titleLabel ? (
            <Title
              className={`section-image-text__content-wrapper__title-wrapper__title`}
              {...title}
              dangerouslySetInnerHTML={{ __html: titleLabel }}
            />
          ) : null}
        </div>

        {paragraphContent ? (
          <Paragraph
            // We override Paragraph's default alignment by following the Title's
            // alignment; this is needed because the WYSIWYG editor won't pass
            // any style for its default alignment (which is left).
            // If the user sets an alignment other than left in the WYSIWYG editor,
            // the resulting HTML with inline styles will override this alignment.
            alignment={title?.alignment}
            as={'div'}
            className={`section-image-text__content-wrapper__paragraph`}
            dangerouslySetInnerHTML={{ __html: paragraphContent }}
            {...paragraph}
          />
        ) : null}

        {cta && ctaLabel ? (
          <Anchor
            wrapper={'div'}
            data-alignment={cta?.alignment}
            className={`section-image-text__content-wrapper__cta link-button-themed`}
            // variant={'themed'}
            as={Link}
            {...cta}
          >
            {ctaLabel}
            <LinkArrowRight
              className={`section-image-text__content-wrapper__cta__icon`}
            />
          </Anchor>
        ) : null}
      </div>

      {imageProps?.src ? (
        <Image
          fit={fit}
          className={`section-image-text__image`}
          as={NextImage}
          imageProps={{
            ...imageProps,
            // If not present (which it will not be,
            // since the CMS is not returning them),
            // it'll take the default value.
            // TODO find out why these props were expected here,
            // @ts-ignore
            width: imageProps.width || 400,
            // @ts-ignore
            height: imageProps.height || 400,
          }}
        />
      ) : null}
    </Section>
  )
}

export default ImageText
